import { z } from 'zod';
import type { InstalledMod } from './mods.js';

export const PortProtocolSchema = z.enum(['udp', 'tcp']);
export const NetworkPortSchema = z.number().int().min(1).max(65535);
export const ExtraPublicPortSchema = NetworkPortSchema.min(24400).max(24499);
export const VoicePresetSchema = z.enum(['simple-voice-chat', 'plasmo-voice']);
export const PublicVoiceHostSchema = z
  .string()
  .max(253)
  .regex(/^[a-z0-9](?:[a-z0-9.-]*[a-z0-9])?$/);
const portFields = {
  id: z.string().regex(/^[a-zA-Z0-9_-]{1,64}$/),
  name: z.string().trim().min(1).max(100),
  protocol: PortProtocolSchema,
  internalPort: NetworkPortSchema,
  preset: VoicePresetSchema.optional(),
};
export const ExtraPortSchema = z.object({ ...portFields, publicPort: ExtraPublicPortSchema }).strict();
export const ExtraPortInputSchema = z.object({ ...portFields, publicPort: ExtraPublicPortSchema.optional() }).strict();
export const ChangePortsParamsSchema = z.object({ extraPorts: z.array(ExtraPortInputSchema).max(100) }).strict();
export const PortsDeploymentSchema = z
  .object({
    extraPorts: z.array(ExtraPortSchema).max(100),
    host: PublicVoiceHostSchema,
  })
  .strict()
  .superRefine(({ extraPorts }, ctx) => {
    const ids = new Set<string>();
    const publicPorts = new Set<number>();
    const bindings = new Set<string>();
    const presets = new Set<VoicePreset>();
    for (const port of extraPorts) {
      const binding = `${port.protocol}:${port.internalPort}`;
      if (
        ids.has(port.id) ||
        publicPorts.has(port.publicPort) ||
        bindings.has(binding) ||
        (port.preset && (presets.has(port.preset) || port.protocol !== 'udp'))
      )
        ctx.addIssue({ code: 'custom', message: 'Duplicate or invalid extra port' });
      ids.add(port.id);
      publicPorts.add(port.publicPort);
      bindings.add(binding);
      if (port.preset) presets.add(port.preset);
    }
  });
export const ForwardSchema = z
  .object({
    protocol: PortProtocolSchema,
    publicPort: NetworkPortSchema,
    address: z.ipv4(),
    internalPort: NetworkPortSchema,
  })
  .strict();
export type ExtraPort = z.infer<typeof ExtraPortSchema>;
export type ExtraPortInput = z.infer<typeof ExtraPortInputSchema>;
export type VoicePreset = z.infer<typeof VoicePresetSchema>;
export type PortsDeployment = z.infer<typeof PortsDeploymentSchema>;
export type Forward = z.infer<typeof ForwardSchema>;

export const voicePresets = [
  { id: 'simple-voice-chat', name: 'Simple Voice Chat', protocol: 'udp', internalPort: 24454 },
  { id: 'plasmo-voice', name: 'Plasmo Voice', protocol: 'udp', internalPort: 60606 },
] as const;

export function suggestedVoicePresets(mods: Pick<InstalledMod, 'slug' | 'env'>[]) {
  const slugs = new Set(mods.filter((m) => m.env.server !== 'unsupported').map((m) => m.slug.toLowerCase()));
  return voicePresets.filter((p) =>
    (p.id === 'simple-voice-chat' ? ['simple-voice-chat', 'voicechat'] : ['plasmo-voice', 'plasmovoice']).some((slug) =>
      slugs.has(slug),
    ),
  );
}

export const PortsViewSchema = z.object({
  extraPorts: z.array(ExtraPortSchema.extend({ address: z.string() })),
  presets: z.array(
    z.object({
      id: VoicePresetSchema,
      name: z.string(),
      protocol: PortProtocolSchema,
      internalPort: NetworkPortSchema,
    }),
  ),
  host: PublicVoiceHostSchema,
});
export type PortsView = z.infer<typeof PortsViewSchema>;

export function allocateExtraPort(used: Iterable<number>): number {
  const occupied = new Set(used);
  for (let port = 24400; port <= 24499; port++) if (!occupied.has(port)) return port;
  throw new Error('追加ポートの公開範囲（24400〜24499）を使い切りました');
}

/** 新規公開番号は Panel が割り当て、既存 ID の番号だけを引き継ぐ。 */
export function resolveExtraPorts(inputs: ExtraPortInput[], current: ExtraPort[], used: Iterable<number>): ExtraPort[] {
  const occupied = new Set(used);
  const reserved = new Set([
    ...occupied,
    ...current.filter((p) => inputs.some((entry) => entry.id === p.id)).map((p) => p.publicPort),
  ]);
  const ids = new Set<string>();
  const bindings = new Set<string>();
  const presets = new Set<VoicePreset>();
  const result: ExtraPort[] = [];
  for (const input of inputs) {
    const entry = ExtraPortInputSchema.parse(input);
    const previous = current.find((p) => p.id === entry.id);
    if (entry.publicPort !== undefined && entry.publicPort !== previous?.publicPort)
      throw new Error('公開ポートは Panel が割り当てます');
    const publicPort = previous?.publicPort ?? allocateExtraPort(reserved);
    const binding = `${entry.protocol}:${entry.internalPort}`;
    if (ids.has(entry.id) || bindings.has(binding) || occupied.has(publicPort))
      throw new Error('ポートが重複しています');
    if (entry.preset && (entry.protocol !== 'udp' || presets.has(entry.preset)))
      throw new Error('プリセットが重複しているか UDP ではありません');
    ids.add(entry.id);
    bindings.add(binding);
    if (entry.preset) presets.add(entry.preset);
    reserved.add(publicPort);
    result.push({ ...entry, publicPort });
  }
  return result;
}
